import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Share2, CheckCircle, Loader2 } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { getSummary, getSummaryMarkdown } from '@/lib/history';
import { resolveDeviceTimezone } from '@/lib/doses';
import { SeverityColors } from '@/lib/theme';
import SeverityBadge from '@/components/SeverityBadge';

function fmtDateTime(iso, tz) {
  try {
    return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: tz }).format(new Date(iso));
  } catch { return iso; }
}

export default function SummaryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const tz = user?.timezone || resolveDeviceTimezone();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const s = await getSummary(tz);
      setData(s);
    } catch (e) {
      setError(e.message || 'Could not generate summary.');
    } finally {
      setLoading(false);
    }
  }, [tz]);

  useEffect(() => { load(); }, [load]);

  const onShare = async () => {
    setSharing(true);
    setError(null);
    try {
      const md = await getSummaryMarkdown(tz);
      if (navigator.share) {
        await navigator.share({ title: 'Heritage Compass Summary', text: md });
        setToast('Shared');
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(md);
        setToast('Copied to clipboard');
      } else {
        setToast('Sharing not available on this device');
      }
      setTimeout(() => setToast(null), 3500);
    } catch (e) {
      if (e.name !== 'AbortError') {
        setError(e.message || 'Could not generate the share file.');
      }
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 pb-12" data-testid="summary-screen">
      <div className="flex items-center gap-2 mb-4">
        <button onClick={() => navigate(-1)} data-testid="summary-back-btn" className="p-1 rounded-lg hover:bg-black/5">
          <ChevronLeft className="h-6 w-6" style={{ color: '#5B2D1F' }} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-extrabold" style={{ color: '#5B2D1F' }} data-testid="summary-screen-title">Doctor summary</h1>
          {data && <p className="text-xs mt-0.5" style={{ color: '#4B6B79' }}>Generated {fmtDateTime(data.generated_at, tz)}</p>}
        </div>
        <button
          onClick={onShare}
          disabled={sharing || loading || !data}
          data-testid="summary-share-btn"
          className="flex items-center gap-1.5 px-4 min-h-[44px] rounded-xl text-base font-extrabold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
          style={{ backgroundColor: '#D77A52' }}
        >
          {sharing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Share2 className="h-4 w-4" /> Share</>}
        </button>
      </div>

      {toast && (
        <div data-testid="summary-toast" className="flex items-center gap-2 p-3 rounded-xl border mb-4" style={{ backgroundColor: '#E4F4EB', borderColor: '#1F7A4A' }}>
          <CheckCircle className="h-4 w-4" style={{ color: '#1F7A4A' }} />
          <span className="text-sm font-bold" style={{ color: '#1F7A4A' }}>{toast}</span>
        </div>
      )}

      {error && (
        <div data-testid="summary-error" className="p-4 rounded-xl border mb-4" style={{ backgroundColor: '#FCE7EA', borderColor: '#B0303A' }}>
          <p className="font-semibold" style={{ color: '#B0303A' }}>{error}</p>
        </div>
      )}

      {loading && (
        <div className="flex flex-col items-center py-16 gap-3">
          <img src="/brand/logo-round.png" alt="" className="w-14 h-14 rounded-full opacity-35" />
          <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#D77A52' }} />
          <p className="text-base" style={{ color: '#4B6B79' }}>Building your summary…</p>
        </div>
      )}

      {!loading && data && (
        <>
          {/* Patient card */}
          <div data-testid="summary-patient-card" className="rounded-2xl p-6 mb-6" style={{ backgroundColor: '#EEF4F7' }}>
            <p className="text-xl font-extrabold mb-1" style={{ color: '#5B2D1F' }} data-testid="summary-patient-name">{data.patient.name || '—'}</p>
            {data.patient.age != null && <p className="text-base" style={{ color: '#0F2A35' }}>Age {data.patient.age}</p>}
            {data.patient.allergies && <p className="text-base mt-0.5" style={{ color: '#0F2A35' }}><strong>Allergies:</strong> {data.patient.allergies}</p>}
            {data.patient.conditions && <p className="text-base mt-0.5" style={{ color: '#0F2A35' }}><strong>Conditions:</strong> {data.patient.conditions}</p>}
          </div>

          {/* Current medications */}
          <p className="text-xs font-extrabold uppercase tracking-widest mb-2 mt-2" style={{ color: '#4B6B79' }}>Current medications</p>
          {data.current_medications.length === 0 ? (
            <div className="rounded-xl border p-4 mb-6" style={{ backgroundColor: '#FFFFFF', borderColor: '#D7E2E8' }}>
              <p style={{ color: '#4B6B79' }}>No medications on file.</p>
            </div>
          ) : (
            <div className="space-y-2 mb-6" data-testid="summary-medications">
              {data.current_medications.map((m, idx) => (
                <div key={`${m.name}-${idx}`} data-testid={`summary-medication-${idx}`} className="flex items-start gap-2 rounded-2xl border p-4" style={{ backgroundColor: '#FFFFFF', borderColor: '#D7E2E8' }}>
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-extrabold" style={{ color: '#0F2A35' }}>{m.name}</p>
                    <p className="text-sm mt-0.5" style={{ color: '#4B6B79' }}>{m.dosage} · {m.frequency}</p>
                    {m.schedule_times?.length > 0 && <p className="text-sm" style={{ color: '#4B6B79' }}>Times: {m.schedule_times.join(', ')}</p>}
                    {m.notes && <p className="text-sm italic mt-1" style={{ color: '#4B6B79' }}>&ldquo;{m.notes}&rdquo;</p>}
                  </div>
                  {m.unverified && (
                    <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full border uppercase tracking-wide" style={{ backgroundColor: '#FFF4DF', borderColor: '#B07A1F', color: '#B07A1F' }}>Unverified</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Active interactions */}
          <p className="text-xs font-extrabold uppercase tracking-widest mb-2" style={{ color: '#4B6B79' }}>Active interactions</p>
          {data.active_interactions.length === 0 ? (
            <div className="rounded-xl border p-4 mb-6" style={{ backgroundColor: '#FFFFFF', borderColor: '#D7E2E8' }}>
              <p style={{ color: '#4B6B79' }}>No active interactions detected in the latest analysis.</p>
            </div>
          ) : (
            <div className="space-y-2 mb-6" data-testid="summary-interactions">
              {data.active_interactions.map((it, idx) => {
                const c = SeverityColors[it.severity] || SeverityColors.uncertain;
                return (
                  <div key={`${it.drug_a}-${it.drug_b}-${idx}`} data-testid={`summary-interaction-${idx}`} className="rounded-2xl border p-4" style={{ backgroundColor: c.bg, borderColor: c.border }}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-base font-extrabold flex-1" style={{ color: c.text }}>{it.drug_a} × {it.drug_b}</span>
                      <SeverityBadge severity={it.severity} size="sm" />
                    </div>
                    <p className="text-base leading-relaxed" style={{ color: c.text }}>{it.summary}</p>
                    <p className="text-sm font-semibold mt-1" style={{ color: c.text }}>Recommendation: {it.recommendation}</p>
                  </div>
                );
              })}
            </div>
          )}

          {/* Adherence */}
          <p className="text-xs font-extrabold uppercase tracking-widest mb-2" style={{ color: '#4B6B79' }}>Adherence — last 30 days</p>
          <div data-testid="summary-adherence" className="flex items-center gap-6 rounded-2xl border p-6 mb-6" style={{ backgroundColor: '#FFFFFF', borderColor: '#D7E2E8' }}>
            <span className="text-5xl font-black" style={{ color: '#5B2D1F' }}>{data.adherence_30d.adherence_pct}%</span>
            <div className="flex-1 space-y-1">
              <p className="text-base" style={{ color: '#4B6B79' }}>Expected: <strong style={{ color: '#0F2A35' }}>{data.adherence_30d.total_expected}</strong></p>
              <p className="text-base" style={{ color: '#4B6B79' }}>Taken: <strong style={{ color: '#1F7A4A' }}>{data.adherence_30d.taken}</strong></p>
              <p className="text-base" style={{ color: '#4B6B79' }}>Missed: <strong style={{ color: '#B0303A' }}>{data.adherence_30d.missed}</strong></p>
              <p className="text-base" style={{ color: '#4B6B79' }}>Skipped: <strong style={{ color: '#5C6B72' }}>{data.adherence_30d.skipped}</strong></p>
            </div>
          </div>

          {/* Recent doses */}
          <p className="text-xs font-extrabold uppercase tracking-widest mb-2" style={{ color: '#4B6B79' }}>Recent doses — last 14 days</p>
          {data.recent_doses_14d.length === 0 ? (
            <div className="rounded-xl border p-4 mb-6" style={{ backgroundColor: '#FFFFFF', borderColor: '#D7E2E8' }}>
              <p style={{ color: '#4B6B79' }}>No dose history recorded.</p>
            </div>
          ) : (
            <div data-testid="summary-doses" className="rounded-2xl border overflow-hidden mb-6" style={{ backgroundColor: '#FFFFFF', borderColor: '#D7E2E8' }}>
              <div className="flex px-4 py-2 gap-2" style={{ backgroundColor: '#EEF4F7' }}>
                <span className="flex-[1.5] text-xs font-extrabold uppercase tracking-wide" style={{ color: '#4B6B79' }}>Medication</span>
                <span className="flex-[2] text-xs font-extrabold uppercase tracking-wide" style={{ color: '#4B6B79' }}>When</span>
                <span className="flex-1 text-xs font-extrabold uppercase tracking-wide" style={{ color: '#4B6B79' }}>Status</span>
              </div>
              {data.recent_doses_14d.map((d, idx) => (
                <div key={`${d.medication_name}-${d.scheduled_for}-${idx}`} data-testid={`summary-dose-${idx}`} className="flex px-4 py-2 gap-2 border-b" style={{ borderColor: '#D7E2E8' }}>
                  <span className="flex-[1.5] text-sm truncate" style={{ color: '#0F2A35' }}>{d.medication_name}</span>
                  <span className="flex-[2] text-sm truncate" style={{ color: '#0F2A35' }}>{fmtDateTime(d.scheduled_for, tz)}</span>
                  <span className={`flex-1 text-sm ${d.status === 'taken' ? 'font-extrabold' : d.status === 'skipped' ? 'font-bold' : ''}`} style={{ color: d.status === 'taken' ? '#1F7A4A' : d.status === 'skipped' ? '#5C6B72' : '#0F2A35' }}>
                    {d.status}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Disclaimer */}
          <div data-testid="summary-disclaimer" className="rounded-xl border p-4" style={{ backgroundColor: '#FFF4DF', borderColor: '#E0B96F' }}>
            <p className="text-sm leading-relaxed" style={{ color: '#5C3B0E' }}>{data.disclaimer}</p>
          </div>
        </>
      )}
    </div>
  );
}
